import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Formik, Form } from "formik";
import { Grid, Modal } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { useSnackbar } from "notistack";
import { useMaterialUIController } from "context";

import AddGrupoEscolar from "./components/grupo_info";
import validations from "./schemas/validations";
import form from "./schemas/form";
import initialValues from "./schemas/initialValues";

import {
  useCreateCarrerasPorGrupoMutation,
  useCreateGrupoEscolarMutation,
  useEditCarrerasPorGrupoMutation,
  useEditGrupoEscolarMutation,
  useGetCiclosQuery,
  useGetGrupoEscolarQuery,
  useLazyGetCarrerasPorGrupoByIdQuery,
} from "services";
import { getModifiedFields } from "helpers/functions";

const NewGrupoEscolar = ({ open, handleClose, id }) => {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const { enqueueSnackbar } = useSnackbar();

  const [idGrupo, setIdGrupo] = useState();
  const [oldValues, setOldValues] = useState();

  const { data: ciclos } = useGetCiclosQuery(undefined, {
    refetchOnReconnect: true,
  });

  const { data: grupos } = useGetGrupoEscolarQuery({
    filter_params: {
      estudiantes: true,
      ordering: "identificador",
      query: "{identificador,url}",
    },
    shape: {
      url: "url",
      identificador: "identificador",
    },
  });

  const [createGrupoEscolar] = useCreateGrupoEscolarMutation();
  const [editGrupoEscolar] = useEditGrupoEscolarMutation();
  const [createGrupoPorCarrera] = useCreateCarrerasPorGrupoMutation();
  const [editGrupoPorCarrera] = useEditCarrerasPorGrupoMutation();

  const { formId, formField } = form;
  const currentValidation = validations[0];

  const [getCarreraPorGrupoById, { data: grupo }] =
    useLazyGetCarrerasPorGrupoByIdQuery();

  const submitForm = async (values, actions) => {
    try {
      if (!id) {
        if (grupos?.some((e) => e.identificador === values.identificador)) {
          const sameIdentificador = grupos.find(
            (e) => e.identificador === values.identificador,
          )?.url;
          await createGrupoPorCarrera({
            ciclo: values.ciclo,
            ciclo_interno: values.ciclo_interno,
            grupo: sameIdentificador,
          }).unwrap();
        } else {
          await createGrupoEscolar(values).unwrap();
        }
        enqueueSnackbar("Grupo Escolar creado", { variant: "success" });
      } else {
        const modifiedFields = getModifiedFields(oldValues, values);
        if (Object.keys(modifiedFields).length !== 0) {
          if (modifiedFields.identificador) {
            await editGrupoEscolar({
              id: idGrupo,
              identificador: modifiedFields.identificador,
            }).unwrap();
          }
          if (modifiedFields.ciclo_interno) {
            await editGrupoPorCarrera({
              id: id,
              ciclo_interno: modifiedFields.ciclo_interno,
            }).unwrap();
          }
          enqueueSnackbar("Grupo Escolar editado", { variant: "success" });
        }
      }

      actions.setSubmitting(false);
      actions.resetForm();
      handleClose();
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        error?.data?.detail || "Error al guardar el Grupo Escolar",
        { variant: "error" },
      );
      actions.setSubmitting(false);
    }
  };

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90%",
    maxWidth: 900,
    boxShadow: 24,
    borderRadius: "12px",
    px: "40px",
    py: "29px",
    outline: "none",
    maxHeight: "95vh",
    overflowY: "auto",
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <MDBox sx={modalStyle} bgColor={darkMode ? "black" : "white"}>
        <MDBox mb={3} textAlign="center">
          <MDTypography variant="h4" fontWeight="bold">
            Grupo Escolar
          </MDTypography>
          <MDTypography variant="body2" color="secondary">
            {!id
              ? "Introduzca la información relacionada al Grupo Escolar por agregar"
              : `Edite la información relacionada al Grupo Escolar: ${grupo?.grupo?.identificador || ""}`}
          </MDTypography>
        </MDBox>
        <Formik
          initialValues={initialValues}
          validationSchema={currentValidation}
          onSubmit={submitForm}
        >
          {({ values, errors, touched, setFieldValue, isSubmitting }) => {
            useEffect(() => {
              if (id) {
                getCarreraPorGrupoById(id)
                  .unwrap()
                  .then((res) => {
                    setOldValues({
                      identificador: res?.grupo?.identificador,
                      ciclo_interno: res?.ciclo_interno?.url,
                    });
                    setFieldValue(
                      formField.identificador.name,
                      res?.grupo?.identificador,
                      true,
                    );
                    setFieldValue(
                      "ciclo_interno",
                      res?.ciclo_interno?.url,
                      true,
                    );
                    setIdGrupo(res?.grupo?.id);
                  });
              }
            }, [
              id,
              getCarreraPorGrupoById,
              setFieldValue,
              formField.identificador.name,
            ]);

            useEffect(() => {
              if (ciclos) {
                setFieldValue("ciclo", ciclos[ciclos?.length - 1]?.url);
              }
            }, [ciclos, setFieldValue]);

            return (
              <Form id={formId} autoComplete="off">
                <MDBox>
                  <AddGrupoEscolar
                    formData={{
                      values,
                      touched,
                      formField,
                      errors,
                      setFieldValue,
                    }}
                  />
                  <Grid
                    item
                    xs={12}
                    display="flex"
                    justifyContent="center"
                    gap={2}
                    mt={4}
                  >
                    <MDButton
                      variant="contained"
                      color="accent"
                      type="submit"
                      disabled={isSubmitting}
                    >
                      Aceptar
                    </MDButton>
                    <MDButton
                      variant="outlined"
                      color="accent"
                      onClick={handleClose}
                    >
                      Cancelar
                    </MDButton>
                  </Grid>
                </MDBox>
              </Form>
            );
          }}
        </Formik>
      </MDBox>
    </Modal>
  );
};

NewGrupoEscolar.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  id: PropTypes.string,
};

export default NewGrupoEscolar;
